import React, { useEffect, useRef, useState } from "react"
import TagManager from "react-gtm-module"
import { PiNotePencilBold } from "react-icons/pi"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"

import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import OtpInput from "@fuse/sharedComponents/OtpInput/OtpInput"
import {
  showErrorToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import FancyText from "@fuse/sharedComponents/typography/FancyText"
import { TEventDetail, TWebUser } from "@letshang/constants"
import { TTemplateEvent } from "@letshang/constants"
import * as Sentry from "@sentry/react"
import {
  PhoneAuthProvider,
  RecaptchaVerifier,
  signInWithCredential,
  signInWithPhoneNumber,
} from "firebase/auth"
import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber"
import { get } from "lodash-es"
import { useRecoilState } from "recoil"
import { ROUTES } from "src/App"

import { screenState, userState } from "@fuse/recoil/signup"

import { ELogGTMEvent } from "@firebaseConfig/analyticsUtil"
import { auth } from "@firebaseConfig/clientApp"

import { getUserInfo, joinEvent } from "@api/firebaseWrappedApis"

import { sentryLog } from "@fuse/utils/common"

import { recaptchaErrorhandler, useRecaptcha } from "../RecaptchaStateProvider"

interface VerifyOtpProps {
  modal?: boolean
  onClose?: () => void
  redirectTo?: string
  template?: TTemplateEvent
  category?: string
  reInitializeTheRecaptchaVerifier: () => void
  // forcVerifyRecaptcha: () => void;
}

const VerifyOtp: React.FC<VerifyOtpProps> = ({
  modal = false,
  onClose = () => {},
  redirectTo,
  template = undefined,
  category = "",
  reInitializeTheRecaptchaVerifier,
  // forcVerifyRecaptcha
}) => {
  const [otp, setOtp] = useState("")
  const { eventId } = useParams()
  const phoneUtil = PhoneNumberUtil.getInstance()

  const [, setStep] = useRecoilState(screenState)

  const [user, setUser] = useRecoilState(userState)

  const [isLoading, setIsLoading] = useState(false)
  const isResendingRef = useRef(false)

  const navigate = useNavigate()

  const VerifyOtp = async () => {
    try {
      setIsLoading(true)

      const verificationId = user?.verificationId

      const credential = PhoneAuthProvider.credential(verificationId, otp)
      const signIn = await signInWithCredential(auth, credential)

      const userInfo = (await getUserInfo({})) as any
      const currentUserInfo = get(userInfo, "userInfo")
      if (currentUserInfo?.displayName) {
        setUser((prev: TWebUser) => ({
          ...prev,
          ...currentUserInfo,
          uid: signIn?.user?.uid,
          name: currentUserInfo?.displayName,
          firstName: currentUserInfo?.displayName?.split(" ")[0],
          lastName: currentUserInfo?.displayName?.split(" ")[1],
          isOnboarded: true,
          isLoggedIn: true,
        }))

        setStep(1)

        if (!modal) {
          if (user?.eventId) {
            await joinEvent({ eventId: user?.eventId })
          }

          if (redirectTo) {
            navigate(redirectTo, {
              state: {
                template: template || {},
                category: category,
              },
            })
          } else {
            navigate(user.redirectUrl || "/")
          }
          return
        } else {
          if (eventId) {
            await joinEvent({ eventId }).then((res: TEventDetail) => {
              if (res?.success)
                showSuccessToast(
                  res?.data?.eventName
                    ? `You successfully joined "${res?.data?.eventName}"`
                    : res?.message,
                )
              else showErrorToast(res?.message)
            })
            if (currentUserInfo?.displayName) onClose()
            else setStep(3)
            return
          }
        }
      }

      setUser((prev: TWebUser) => ({
        ...prev,
        verified: true,
        uid: signIn?.user?.uid,
      }))
      setStep(3)
    } catch (error: any) {
      console.error(error)
      const errMsg = error.message

      if (errMsg.includes("auth/invalid-verification-code")) {
        showErrorToast("Invalid OTP")
      } else if (errMsg.includes("auth/code-expired")) {
        showErrorToast("Code expired, please try again")
      }
    } finally {
      setOtp("")
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (otp.length === 6) {
      VerifyOtp()
    }
  }, [otp])

  useEffect(() => {
    if (!user.phone) {
      setStep(1)
    }
  }, [user])

  const { verifier, recaptchaVerifiedState } = useRecaptcha()
  async function resendOtp() {
    // Prevent multiple resend requests
    if (isResendingRef.current) {
      return
    }

    try {
      isResendingRef.current = true
      setIsLoading(true)
      if (!recaptchaVerifiedState?.current) {
        // here we can add logic to re-initialize the instance and verify it from new verifier
        const wait = await new Promise<{
          isReloaded: boolean
          message?: string
        }>((resolve, reject) => {
          showErrorToast("Your verification expired.", {
            text2: "Please reload the page to sign in again.",
            buttonText: "Reload Page",
            onClose: () => {
              reject({
                isReloaded: false,
                message: "The user denied to re-authenticate the re-captcha",
                code: "auth/recaptcha-expired/re-auth-denied",
              })
            },
            onButtonClick: () => {
              reInitializeTheRecaptchaVerifier()
              if (verifier?.current) {
                verifier?.current.render() // to render the re-captcha again
                resolve({
                  isReloaded: true,
                })
              } else {
                reject({
                  isReloaded: false,
                  code: "auth/recaptcha-expired/verifier-not-available",
                  message:
                    "Verifier instance is not available even after re-initialization",
                })
              }
            },
          })
        })

        if (!wait?.isReloaded) {
          return
        }
      }
      const confirmation = await signInWithPhoneNumber(
        auth,
        user.dial_code + user.phone,
        verifier?.current as RecaptchaVerifier,
      )
      setUser((prev: TWebUser) => ({
        ...prev,
        verificationId: confirmation.verificationId,
      }))
      setStep(2)
    } catch (error: any) {
      console.error(error)
      sentryLog({ error, component: "VerifyOtp" })
      recaptchaErrorhandler(error)
    } finally {
      setIsLoading(false)
      isResendingRef.current = false
    }
  }

  const getFormattedPhone = () => {
    if (!user?.phone) {
      return ""
    }

    const parsedNumber = phoneUtil.parse(user.phone, user?.country)

    let formattedDisplay = phoneUtil.format(
      parsedNumber,
      PhoneNumberFormat.NATIONAL,
    )

    if (formattedDisplay.startsWith("0")) {
      formattedDisplay = formattedDisplay.replace(/^0/, "")
    }

    return formattedDisplay
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen w-full">
      <div className="w-[90%] max-w-sm mb-5 md:mb-10 text-start">
        <p className="text-white/30 text-left text-sm mt-10 md:mt-0 lg:mt-0 font-medium">
          STEP 1
        </p>
        <FancyText className="text-xl md:text-2.5xl mt-[10px] text-white font-semibold">
          Signing you up
        </FancyText>
      </div>
      <div className="relative z-[10]">{isLoading && <LoadingSpinner />}</div>
      <div className="w-[90%] max-w-sm bg-white/10 relative rounded-2xl p-10 space-y-6">
        {/* Title */}
        <div className="text-left">
          <h2 className="text-white text-2xl font-semibold">
            We've sent an SMS Code
          </h2>
          <div className="flex flex-row items-center mt-2">
            <p className=" text-white/35 text-sm">
              To the number:{" "}
              <span className="text-white text-base">
                {user.dial_code} {getFormattedPhone()}
              </span>
            </p>
            <div className="ml-2 cursor-pointer" onClick={() => setStep(1)}>
              <PiNotePencilBold className=" text-white text-lg" />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 pb-24">
          <OtpInput
            value={otp}
            valueLength={6}
            onChange={(value) => {
              setOtp(value)
            }}
          />
        </div>

        <div className="flex flex-col items-center justify-center">
          <p className="text-white">Code not received?</p>
          <div
            onClick={() => {
              resendOtp()
            }}
            className="cursor-pointer"
          >
            <p className="text-white/35 underline">Request again</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VerifyOtp
